// NSB Verified Badge — fills from bottom to top based on verification level
// 0% = grey, 25/50/75/100% = gradient fill from bottom

export function NSBVerifiedBadge({ percent = 0, size = "sm" }) {
  const sizes = {
    xs: { outer: 20, icon: 10 },
    sm: { outer: 28, icon: 14 },
    md: { outer: 48, icon: 24 },
    lg: { outer: 80, icon: 40 },
    xl: { outer: 112, icon: 56 },
  };

  const s = sizes[size] || sizes.sm;
  const r = s.outer / 2;
  const clipId = `badge-clip-${size}-${percent}`;

  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: s.outer, height: s.outer }}
      title={percent >= 100 ? "NSB Fully Verified" : `${percent}% Verified`}
      data-testid="nsb-verified-badge"
    >
      <svg width={s.outer} height={s.outer} viewBox={`0 0 ${s.outer} ${s.outer}`}>
        <defs>
          {/* Gradient for the color fill */}
          <linearGradient id={`grad-${clipId}`} x1="0" y1="1" x2="0.5" y2="0">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="50%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
          {/* Clip rect that fills from bottom based on percent */}
          <clipPath id={clipId}>
            <rect x="0" y={s.outer * (1 - percent / 100)} width={s.outer} height={s.outer * (percent / 100)} />
          </clipPath>
        </defs>
        {/* Grey background circle */}
        <circle cx={r} cy={r} r={r} fill="#374151" opacity="0.5" />
        {/* Gradient fill circle - clipped from bottom */}
        {percent > 0 && (
          <circle cx={r} cy={r} r={r} fill={`url(#grad-${clipId})`} clipPath={`url(#${clipId})`} />
        )}
        {/* Glow for 100% */}
        {percent >= 100 && (
          <circle cx={r} cy={r} r={r} fill="none" stroke="rgba(168,85,247,0.4)" strokeWidth="2" />
        )}
      </svg>
      {/* Shield icon centered */}
      <div className="absolute inset-0 flex items-center justify-center">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          width={s.icon}
          height={s.icon}
          className={percent > 0 ? "opacity-100" : "opacity-40"}
        >
          <path d="M12 2L3.5 6.5V11.5C3.5 16.45 7.02 21.08 12 22C16.98 21.08 20.5 16.45 20.5 11.5V6.5L12 2Z" />
          <path d="M8.5 12.5L11 15L16 10" />
        </svg>
      </div>
      {/* Glow shadow for high levels */}
      {percent >= 75 && (
        <div
          className="absolute inset-0 rounded-full"
          style={{ boxShadow: `0 0 ${percent >= 100 ? 24 : 12}px rgba(168,85,247,${percent >= 100 ? 0.4 : 0.2})` }}
        />
      )}
    </div>
  );
}

// Inline badge for next to metric values - always full color
export function NSBVerifiedInline({ verified = false }) {
  if (!verified) return null;
  return (
    <span
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex-shrink-0"
      title="NSB Verified"
      data-testid="nsb-verified-inline"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width={9} height={9}>
        <path d="M12 2L3.5 6.5V11.5C3.5 16.45 7.02 21.08 12 22C16.98 21.08 20.5 16.45 20.5 11.5V6.5L12 2Z" />
        <path d="M8.5 12.5L11 15L16 10" />
      </svg>
    </span>
  );
}

// Verification tiers definition
export const VERIFICATION_TIERS = [
  { key: "velocity", label: "Velocity Captured", desc: "Max Velo or Exit Velo verified", check: (m) => !!(m.fastball_velocity || m.exit_velocity) },
  { key: "spin_ext", label: "Spin & Extension", desc: "Spin rate and extension verified", check: (m) => !!(m.spin_rate || m.max_spin) && !!m.extension },
  { key: "speed", label: "Speed Verified", desc: "60-yard dash time captured", check: (m) => !!m.sixty_time },
  { key: "grades", label: "Tool Grades", desc: "NSB scout tool grades (20-80 scale)", check: (m, g) => Object.keys(g || {}).length >= 3 },
];

export function getVerificationLevel(metrics, toolGrades) {
  const m = metrics || {};
  const g = toolGrades || {};
  const completed = VERIFICATION_TIERS.filter(t => t.check(m, g));
  return {
    completed: completed.length,
    total: VERIFICATION_TIERS.length,
    percent: Math.round((completed.length / VERIFICATION_TIERS.length) * 100),
    tiers: VERIFICATION_TIERS.map(t => ({ ...t, done: t.check(m, g) })),
    nextStep: VERIFICATION_TIERS.find(t => !t.check(m, g)),
  };
}
